import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Button, Tabs, Table, Checkbox, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCareTeamAssignableOrganisationUsers } from '@redux/careTeam/actions';
import {
  selectCareTeamAssignableOrganisationUsers,
  selectCareTeamAssignableOrganisationUsersError,
} from '@redux/careTeam/reducers';
import useDirectoryOrganisations from '@hooks/useDirectoryOrganisations';
import './style.less';

const { TabPane } = Tabs;

const formLayout = {
  layout: 'vertical',
};

function buildPickerRows({
  assignableOrganisationUsers,
  organisationNameBySlug,
  member,
  isMemberDashboardUser,
}) {
  const gatedAssignableRows = (assignableOrganisationUsers || [])
    .filter(user => organisationNameBySlug[user.organisation])
    .map(user => ({
      ...user,
      organisationName: organisationNameBySlug[user.organisation],
    }));

  if (!isMemberDashboardUser || !member?.staffUserId) {
    return gatedAssignableRows;
  }

  const selectedId = String(member.staffUserId);
  if (gatedAssignableRows.some(user => String(user.id) === selectedId)) {
    return gatedAssignableRows;
  }

  const fromAssignable = (assignableOrganisationUsers || []).find(
    user => String(user.id) === selectedId,
  );
  const organisationName =
    organisationNameBySlug[member.staffOrganisationId] || member.businessName || '';

  return [
    {
      id: member.staffUserId,
      organisation: member.staffOrganisationId,
      name: fromAssignable?.name || member.name || '',
      email: fromAssignable?.email || member.email || '',
      phoneNumber: fromAssignable?.phoneNumber || member.phone || '',
      organisationName,
    },
    ...gatedAssignableRows,
  ];
}

function AddEditMemberModal({ visible, member, onSave, onDelete, onCancel, form }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { getFieldDecorator, validateFields, setFieldsValue, resetFields, isFieldsTouched } = form;

  const isNewMember = !member;
  const hasExistingRole = !!member?.roleId;
  const hasExistingMember = !!member?.careTeamMemberId;
  const isPathwayRole = member?.createdByType === 'pathway_assignment';
  const isMemberDashboardUser = !!member?.staffUserId;

  const [activeTab, setActiveTab] = useState('select');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectUserModified, setSelectUserModified] = useState(false);

  const [assignableLoading, assignableOrganisationUsers] = useSelector(
    selectCareTeamAssignableOrganisationUsers,
  );
  const assignableError = useSelector(selectCareTeamAssignableOrganisationUsersError);
  // Subscribe so directory fetch completion re-renders this modal.
  const directoryState = useSelector(state => state.directoryOrganisations);
  const getDirectoryOrganisation = useDirectoryOrganisations();

  const organisationNameBySlug = {};
  let directoryLoading = false;
  const relevantOrganisationSlugs = new Set();
  (assignableOrganisationUsers || []).forEach(user => {
    if (user.organisation) relevantOrganisationSlugs.add(user.organisation);
  });
  if (isMemberDashboardUser && member?.staffOrganisationId) {
    relevantOrganisationSlugs.add(member.staffOrganisationId);
  }
  relevantOrganisationSlugs.forEach(slug => {
    // Kick off directory fetch when needed; read settled state from the subscription.
    getDirectoryOrganisation(slug);
    const loading = directoryState.loading[slug];
    if (loading !== false) {
      directoryLoading = true;
    }
    const organisation = directoryState.byId[slug];
    if (organisation?.organisation_name) {
      organisationNameBySlug[slug] = organisation.organisation_name;
    }
  });

  const tableLoading = assignableLoading || directoryLoading;
  const tableRows = buildPickerRows({
    assignableOrganisationUsers,
    organisationNameBySlug,
    member,
    isMemberDashboardUser,
  });

  useEffect(() => {
    if (!visible) return;
    dispatch(fetchCareTeamAssignableOrganisationUsers());

    setSelectUserModified(false);

    if (isNewMember || isMemberDashboardUser) {
      setActiveTab('select');
    } else {
      setActiveTab('details');
    }

    if (isMemberDashboardUser) {
      setSelectedUserId(String(member.staffUserId));
    } else {
      setSelectedUserId(null);
    }

    if (member) {
      const fields = {
        roleName: member.roleName || '',
        roleDescription: member.roleDescription || '',
      };
      if (!isMemberDashboardUser) {
        fields.name = member.name || '';
        fields.email = member.email || '';
        fields.phone = member.phone || '';
        fields.businessName = member.businessName || '';
      } else {
        fields.name = '';
        fields.email = '';
        fields.phone = '';
        fields.businessName = '';
      }
      resetFields();
      setFieldsValue(fields);
    } else {
      resetFields();
    }
  }, [visible, member]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUserToggle = userId => {
    setSelectedUserId(prev => (prev === userId ? null : userId));
    setSelectUserModified(true);
  };

  const resolveBusinessName = user => {
    const resolvedName = organisationNameBySlug[user.organisation];
    if (resolvedName) {
      return resolvedName;
    }
    if (
      isMemberDashboardUser &&
      member &&
      String(member.staffUserId) === String(user.id) &&
      member.staffOrganisationId === user.organisation
    ) {
      return member.businessName || '';
    }
    return '';
  };

  const handleSave = () => {
    if (activeTab === 'select') {
      if (!selectUserModified && !(isMemberDashboardUser && selectedUserId !== null)) {
        onCancel();
        return;
      }
      if (selectedUserId === null) {
        if (hasExistingMember) {
          onDelete(member);
        } else {
          onCancel();
        }
        return;
      }
      const user = tableRows.find(u => String(u.id) === selectedUserId);
      if (!user) return;
      validateFields(['roleName', 'roleDescription'], (err, values) => {
        if (!err) {
          onSave({
            name: user.name,
            email: user.email,
            phone: user.phoneNumber || '',
            businessName: resolveBusinessName(user),
            staffUserId: Number(user.id),
            staffOrganisationId: user.organisation,
            roleName: values.roleName,
            roleDescription: values.roleDescription,
          });
        }
      });
    } else {
      if (!isFieldsTouched()) {
        onCancel();
        return;
      }
      validateFields((err, values) => {
        if (err) return;
        const { name, email, phone, businessName, roleName, roleDescription } = values;
        const allEmpty = !name && !email && !phone && !businessName;
        if (allEmpty) {
          if (hasExistingMember) {
            onDelete(member);
          } else {
            onCancel();
          }
          return;
        }
        onSave({
          name,
          email,
          phone,
          businessName,
          roleName,
          roleDescription,
          staffUserId: null,
          staffOrganisationId: null,
        });
      });
    }
  };

  const dashboardUserColumns = [
    {
      title: '',
      key: 'checkbox',
      width: 40,
      render: (_, user) => (
        <Checkbox
          checked={String(user.id) === selectedUserId}
          onChange={() => handleUserToggle(String(user.id))}
          onClick={e => e.stopPropagation()}
        />
      ),
    },
    {
      title: t('cards:CareTeamMembers.modal.dashboardUsers.name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('cards:CareTeamMembers.modal.dashboardUsers.email'),
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: t('cards:CareTeamMembers.modal.assignableOrganisationUsers.organisation'),
      dataIndex: 'organisationName',
      key: 'organisation',
    },
  ];

  const isEditing = !!member;

  return (
    <Modal
      title={t(
        isEditing
          ? 'cards:CareTeamMembers.modal.editTitle'
          : 'cards:CareTeamMembers.modal.addTitle',
      )}
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={600}
      className="add-edit-member-modal"
    >
      {isPathwayRole && (
        <p className="member-from-pathway-text">
          {t('cards:CareTeamMembers.modal.pathwayRoleNote')}
        </p>
      )}

      <Form {...formLayout}>
        <Form.Item label={t('cards:CareTeamMembers.modal.form.roleName')}>
          {getFieldDecorator('roleName', {
            rules: [
              {
                required: isNewMember,
                message: t('cards:CareTeamMembers.modal.form.roleNameRequired'),
              },
            ],
          })(<Input type="text" disabled={hasExistingRole} />)}
        </Form.Item>

        <Form.Item label={t('cards:CareTeamMembers.modal.form.roleDescription')}>
          {getFieldDecorator('roleDescription')(<Input type="text" disabled={hasExistingRole} />)}
        </Form.Item>
      </Form>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab={t('cards:CareTeamMembers.modal.tabs.selectUser')} key="select">
          {assignableError && (
            <Alert
              type="error"
              showIcon
              message={t('cards:CareTeamMembers.modal.assignableOrganisationUsersError')}
              style={{ marginBottom: 12 }}
            />
          )}
          <Table
            columns={dashboardUserColumns}
            dataSource={tableRows}
            rowKey="id"
            pagination={false}
            size="small"
            loading={tableLoading}
            onRow={user => ({
              onClick: () => handleUserToggle(String(user.id)),
              style: { cursor: 'pointer' },
            })}
          />
        </TabPane>
        <TabPane tab={t('cards:CareTeamMembers.modal.tabs.enterDetails')} key="details">
          <Form {...formLayout}>
            <Form.Item label={t('cards:CareTeamMembers.modal.form.name')}>
              {getFieldDecorator('name')(<Input type="text" />)}
            </Form.Item>

            <Form.Item label={t('cards:CareTeamMembers.modal.form.email')}>
              {getFieldDecorator('email', {
                rules: [
                  {
                    type: 'email',
                    message: t('cards:CareTeamMembers.modal.form.emailInvalid'),
                  },
                ],
              })(<Input type="email" />)}
            </Form.Item>

            <Form.Item label={t('cards:CareTeamMembers.modal.form.phone')}>
              {getFieldDecorator('phone', {
                rules: [
                  {
                    pattern: /^\+[\d\s]+$/,
                    message: t('cards:CareTeamMembers.modal.form.phoneInvalid'),
                  },
                ],
              })(<Input type="tel" />)}
            </Form.Item>

            <Form.Item label={t('cards:CareTeamMembers.modal.form.businessName')}>
              {getFieldDecorator('businessName')(<Input type="text" />)}
            </Form.Item>
          </Form>
        </TabPane>
      </Tabs>

      <div className="modal-footer">
        {hasExistingMember && (
          <Button type="danger" onClick={() => onDelete(member)} style={{ marginRight: 'auto' }}>
            {t('cards:CareTeamMembers.modal.delete')}
          </Button>
        )}
        <div style={{ marginLeft: 'auto' }}>
          <Button onClick={onCancel} style={{ marginRight: '10px' }}>
            {t('cards:CareTeamMembers.modal.cancel')}
          </Button>
          <Button type="primary" onClick={handleSave}>
            {t('cards:CareTeamMembers.modal.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

const WrappedAddEditMemberModal = Form.create()(AddEditMemberModal);
export default WrappedAddEditMemberModal;
